import React, { ReactNode } from 'react';
import { Cell } from 'react-table';
import _round from 'lodash/round';

import {
    formatNumberCompact,
    getGapAnalysisStatementSelectionLogic,
} from '@trqk/utils';

import { gapBarHeight, gapBarMaxWidth, rowHeight } from '../../constants';
import { PerformancesTableButton } from '../../../../../../../nav';
import { URL_PARAMS, MODAL_IDS } from '../../../../../../../../constants';
import { useModal } from '../../../../../../../../hooks';
import { getFeatureFlags } from '../../../../../../../../featureFlags';

interface PerformancesCellProps {
    cell: Cell<TrqkGapChannel, GapChannelPerformancesCellValue>;
}

/**
 * Displays the number of performances that will be added if the current channel is included in the
 * Gap Analysis report.
 */
export const PerformancesCell = ({
    cell,
}: PerformancesCellProps): JSX.Element => {
    const { gapAnalysisLinksToPerformancesTable } = getFeatureFlags();
    const { remove: removeModal } = useModal();
    const {
        value: {
            channelNameDetection,
            gapDenominator,
            performancesMatchedTotal,
            statementId,
        },
    } = cell;
    const gapBarWidthRaw = _round(
        (performancesMatchedTotal / gapDenominator) * gapBarMaxWidth,
    );
    const minGapBarWidth = Math.max(gapBarWidthRaw, 1);
    const gapBarWidth = performancesMatchedTotal > 0 ? minGapBarWidth : 0;

    let data: ReactNode = formatNumberCompact(performancesMatchedTotal);

    const statementSelectionLogic =
        getGapAnalysisStatementSelectionLogic(statementId);

    if (gapAnalysisLinksToPerformancesTable && performancesMatchedTotal > 0) {
        data = (
            <span>
                <PerformancesTableButton
                    onClick={() => {
                        removeModal(MODAL_IDS.MANAGE_GAP_ANALYSIS, {
                            canceled: true,
                            success: false,
                        });
                    }}
                    urlSearchParams={[
                        {
                            key: URL_PARAMS.CHANNEL,
                            value: channelNameDetection,
                        },
                    ]}
                    statementSelectionLogic={statementSelectionLogic}
                >
                    <span className="underline text-primary-500">{`${formatNumberCompact(
                        performancesMatchedTotal,
                    )}`}</span>
                </PerformancesTableButton>
            </span>
        );
    }

    return (
        <div
            className="flex items-center justify-between pr-2 border-r border-tertiary-100"
            style={{ height: rowHeight }}
        >
            <div
                className="flex justify-start"
                style={{ maxWidth: gapBarMaxWidth }}
            >
                <div
                    className="bg-primary-500"
                    style={{ width: gapBarWidth, height: gapBarHeight }}
                />
            </div>
            <div className="flex items-center text-right">{data}</div>
        </div>
    );
};
